import React, { useCallback, useEffect, useState } from "react";
import { Container, Form } from "react-bootstrap";
import {
  AlertCircle,
  Cloud,
  CloudRain,
  Droplets,
  Eye,
  Gauge,
  MapPin,
  RefreshCw,
  Sun,
  Wind,
} from "lucide-react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import "moment/locale/ar";
import "moment/locale/fr";

import api from "../api/api";
import "./Weather.css";

const getForecastIcon = (weatherCode, size = 48) => {
  const code = Number(weatherCode);
  if (
    [51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99].includes(
      code
    )
  ) {
    return <CloudRain size={size} aria-hidden="true" />;
  }
  if ([1, 2, 3, 45, 48].includes(code)) {
    return <Cloud size={size} aria-hidden="true" />;
  }
  return <Sun size={size} aria-hidden="true" />;
};

const Weather = () => {
  const { t, i18n } = useTranslation();
  let offer = "1";

  try {
    const rawUser = localStorage.getItem("user");
    if (rawUser) {
      const parsed = JSON.parse(rawUser);
      if (parsed?.offer_type) offer = parsed.offer_type;
    }
  } catch (error) {
    offer = "1";
  }

  const [coord, setCoord] = useState({ lat: "36.799746", lon: "10.186000" });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isWeatherLoading, setIsWeatherLoading] = useState(false);
  const [farms, setFarms] = useState([]);
  const [farmSelected, setFarmSelected] = useState("");
  const [weatherData, setWeatherData] = useState([]);
  const [currentWeatherData, setCurrentWeatherData] = useState({});
  const [todayWeatherForecast, setTodayWeatherForecast] = useState([]);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [lang, setLang] = useState(i18n.language || "en");

  const getFarms = async () => {
    try {
      setError(null);
      setErrorType(null);
      const response = await api.get("/farm/farms");
      const loadedFarms = Array.isArray(response.data?.farms)
        ? response.data.farms
        : [];
      setFarms(loadedFarms);

      if (loadedFarms.length > 0) {
        const firstFarm = loadedFarms[0];
        const lat =
          firstFarm?.city?.lat ??
          firstFarm?.Latitude ??
          firstFarm?.latitude ??
          firstFarm?.lat;
        const lon =
          firstFarm?.city?.lon ??
          firstFarm?.Longitude ??
          firstFarm?.longitude ??
          firstFarm?.lon;

        if (lat !== undefined && lon !== undefined) {
          setCoord({ lat, lon });
        }
        setFarmSelected(firstFarm?.name ?? "");
      }
    } catch (requestError) {
      console.error("Error fetching farms:", requestError);
      setError(
        t("weather_farms_error", {
          defaultValue: "Unable to load farm locations",
        })
      );
      setErrorType("farms");
    }
  };

  const fetchWeatherData = useCallback(
    async (lat, lon) => {
      const normalizedLat =
        lat !== undefined && lat !== null ? String(lat).trim() : "";
      const normalizedLon =
        lon !== undefined && lon !== null ? String(lon).trim() : "";
      if (!normalizedLat || !normalizedLon) return;

      setIsWeatherLoading(true);
      setError(null);
      setErrorType(null);

      try {
        const currentResponse = await api.post("/weather/get-data", {
          type: "day",
          lat: normalizedLat,
          lon: normalizedLon,
        });
        const current = currentResponse.data?.data;
        const currentMain = current?.main ?? {};
        const currentCondition = current?.weather?.[0] ?? {};

        setCurrentWeatherData({
          city: current?.name ?? "",
          wind: current?.wind?.speed ?? 0,
          windDirection: current?.wind?.deg ?? 0,
          date: new Date((current?.dt ?? Date.now() / 1000) * 1000),
          temp:
            currentMain.feels_like !== undefined
              ? Number(currentMain.feels_like).toFixed(1)
              : "N/A",
          tempMax:
            currentMain.temp_max !== undefined
              ? Number(currentMain.temp_max).toFixed(1)
              : "N/A",
          tempMin:
            currentMain.temp_min !== undefined
              ? Number(currentMain.temp_min).toFixed(1)
              : "N/A",
          main: currentCondition.main ?? "",
          description: currentCondition.description ?? "",
          icon: currentCondition.icon ?? "",
          humidity: currentMain.humidity ?? 0,
          pressure: currentMain.pressure ?? 0,
          visibility: current?.visibility ?? 0,
          rain: current?.rain
            ? current.rain["1h"] ?? current.rain["3h"] ?? 0
            : 0,
        });

        const [forecastResponse, agronomicForecastResponse] = await Promise.all([
          api.post("/weather/get-data", {
            type: "forecast",
            lat: normalizedLat,
            lon: normalizedLon,
          }),
          api.post("/weather/agronomic-forecast", {
            lat: normalizedLat,
            lon: normalizedLon,
          }),
        ]);
        const forecastList = Array.isArray(forecastResponse.data?.data?.list)
          ? forecastResponse.data.data.list
          : [];
        const agronomicDays = Array.isArray(
          agronomicForecastResponse.data?.data?.forecast
        )
          ? agronomicForecastResponse.data.data.forecast
          : [];
        const dailyForecast = agronomicDays.map((day) => {
          const tempMax = Number(day.temperature_max);
          const tempMin = Number(day.temperature_min);
          const hasTemperatures = Number.isFinite(tempMax) && Number.isFinite(tempMin);

          return {
            date: day.date ? new Date(`${day.date}T12:00:00`) : new Date(),
            temp: hasTemperatures ? Math.round((tempMax + tempMin) / 2) : "N/A",
            temp_max: Number.isFinite(tempMax) ? Math.round(tempMax) : "N/A",
            temp_min: Number.isFinite(tempMin) ? Math.round(tempMin) : "N/A",
            humidity: day.humidity_max ?? 0,
            wind: day.windspeed ?? 0,
            precipitation: day.precipitation_sum,
            precipitationProbability: day.precipitation_probability,
            weatherCode: day.weather_code,
          };
        });

        setWeatherData(dailyForecast);
        setTodayWeatherForecast(forecastList.slice(0, 4));
      } catch (requestError) {
        console.error("Error fetching weather data:", requestError);

        if (requestError.response?.status === 404) {
          setError(
            t("weather_location_error", {
              defaultValue: "Weather data is not available for this location.",
            })
          );
          setErrorType("location");
        } else if (
          requestError.code === "NETWORK_ERROR" ||
          !navigator.onLine
        ) {
          setError(
            t("weather_network_error", {
              defaultValue: "No internet connection.",
            })
          );
          setErrorType("network");
        } else if (requestError.response?.status >= 500) {
          setError(
            t("weather_service_error", {
              defaultValue: "The weather service is temporarily unavailable.",
            })
          );
          setErrorType("timeout");
        } else {
          setError(
            t("weather_general_error", {
              defaultValue: "Failed to load weather data.",
            })
          );
          setErrorType("general");
        }
      } finally {
        setIsWeatherLoading(false);
        setIsInitialLoad(false);
      }
    },
    [t]
  );

  useEffect(() => {
    getFarms();
  }, []);

  useEffect(() => {
    if (coord.lat && coord.lon) fetchWeatherData(coord.lat, coord.lon);
  }, [coord.lat, coord.lon, fetchWeatherData]);

  useEffect(() => {
    setLang(i18n.language);
    moment.locale(i18n.language === "en" ? "en-gb" : i18n.language);
  }, [i18n.language]);

  const handleFarmChange = (event) => {
    const selectedValue = event.target.value;
    if (!selectedValue) return;

    const [lat, lon] = selectedValue.split(",");
    setCoord({ lat, lon });
    setFarmSelected(event.target.selectedOptions[0].text);
  };

  const getWeatherIcon = (iconCode, size = 48) => {
    if (iconCode?.includes("02") || iconCode?.includes("03")) {
      return <Cloud size={size} aria-hidden="true" />;
    }
    if (iconCode?.includes("09") || iconCode?.includes("10")) {
      return <CloudRain size={size} aria-hidden="true" />;
    }
    return <Sun size={size} aria-hidden="true" />;
  };

  const getErrorTips = (type) => {
    if (type === "timeout") {
      return [
        t("weather_tip_wait", {
          defaultValue: "Wait a few minutes and try again.",
        }),
        t("weather_tip_recover", {
          defaultValue: "The service usually recovers automatically.",
        }),
      ];
    }
    if (type === "network") {
      return [
        t("weather_tip_connection", {
          defaultValue: "Check your internet connection.",
        }),
      ];
    }
    if (type === "location") {
      return [
        t("weather_tip_location", {
          defaultValue: "Choose another farm or check its coordinates.",
        }),
      ];
    }
    return [
      t("weather_tip_refresh", { defaultValue: "Refresh the weather data." }),
    ];
  };

  const updatedAt = currentWeatherData.date
    ? moment(currentWeatherData.date).locale(lang).format("LT")
    : "—";
  const hasCurrentWeather =
    currentWeatherData.temp !== undefined && currentWeatherData.temp !== "";

  return (
    <Container fluid className="weather-page">
      <header className="weather-page-header">
        <div className="weather-page-heading">
          <span className="weather-page-eyebrow">SMARTFARM</span>
          <h1>{t("Weather_Forecast")}</h1>
          <p>{t("overview")}</p>
        </div>
        <div className="weather-page-actions">
          <span className="weather-page-updated">
            <span className="weather-page-live-dot" />
            {t("last_update", { defaultValue: "Last update" })}: {updatedAt}
          </span>
          <button
            type="button"
            onClick={() => fetchWeatherData(coord.lat, coord.lon)}
            disabled={isWeatherLoading}
          >
            <RefreshCw
              size={16}
              className={isWeatherLoading ? "is-spinning" : ""}
              aria-hidden="true"
            />
            {t("admin_overview_refresh", { defaultValue: "Refresh" })}
          </button>
        </div>
      </header>

      {offer === "2" && farms.length > 0 && (
        <section className="weather-farm-selector">
          <div className="weather-farm-selector-copy">
            <span className="weather-farm-icon">
              <MapPin size={18} aria-hidden="true" />
            </span>
            <div>
              <span>{t("Select_Farm")}</span>
              <strong>{farmSelected || t("Select_Farm")}</strong>
            </div>
          </div>
          <Form.Select
            onChange={handleFarmChange}
            value={`${coord.lat},${coord.lon}`}
            disabled={isWeatherLoading}
            aria-label={t("Select_Farm")}
          >
            <option value="">{t("Select_Farm")}</option>
            {farms.map((farm) => {
              const lat =
                farm?.Latitude ??
                farm?.city?.lat ??
                farm?.latitude ??
                farm?.lat ??
                "";
              const lon =
                farm?.Longitude ??
                farm?.city?.lon ??
                farm?.longitude ??
                farm?.lon ??
                "";
              return (
                <option key={farm.uid || farm.id} value={`${lat},${lon}`}>
                  {farm.name}
                </option>
              );
            })}
          </Form.Select>
        </section>
      )}

      {isWeatherLoading && !isInitialLoad && (
        <div className="weather-loading-banner" role="status">
          <span className="weather-loading-spinner" />
          {t("loading", { defaultValue: "Loading weather data..." })}
        </div>
      )}

      {error && (
        <section className="weather-error-card" role="alert">
          <span className="weather-error-icon">
            <AlertCircle size={21} aria-hidden="true" />
          </span>
          <div>
            <h2>
              {t("weather_unavailable", {
                defaultValue: "Weather data unavailable",
              })}
            </h2>
            <p>{error}</p>
            <ul>
              {getErrorTips(errorType).map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => fetchWeatherData(coord.lat, coord.lon)}
              disabled={isWeatherLoading}
            >
              <RefreshCw size={15} aria-hidden="true" />
              {t("Try_Again", { defaultValue: "Try again" })}
            </button>
          </div>
        </section>
      )}

      {!error && isInitialLoad && (
        <div className="weather-initial-loading" role="status">
          <span className="weather-loading-spinner" />
          <p>{t("loading", { defaultValue: "Loading weather data..." })}</p>
        </div>
      )}

      {!error && hasCurrentWeather && (
        <>
          <section className="weather-current-card">
            <div className="weather-current-primary">
              <span className="weather-current-icon">
                {currentWeatherData.icon ? (
                  <img
                    src={`https://openweathermap.org/img/wn/${currentWeatherData.icon}@2x.png`}
                    alt={currentWeatherData.description || "weather"}
                  />
                ) : (
                  getWeatherIcon(currentWeatherData.icon, 52)
                )}
              </span>
              <div className="weather-current-temperature">
                <strong>
                  {currentWeatherData.temp !== "N/A"
                    ? Math.round(Number(currentWeatherData.temp))
                    : "N/A"}
                  <sup>°</sup>
                </strong>
                <span>{currentWeatherData.description}</span>
              </div>
              <div className="weather-current-location">
                <MapPin size={16} aria-hidden="true" />
                <div>
                  <h2>{farmSelected || currentWeatherData.city || "—"}</h2>
                  <p>
                    H: {currentWeatherData.tempMax}° · L: {currentWeatherData.tempMin}°
                  </p>
                </div>
              </div>
            </div>

            <div className="weather-current-stats">
              <article>
                <span><Droplets size={18} aria-hidden="true" /></span>
                <div><strong>{currentWeatherData.humidity}%</strong><small>{t("Humidity")}</small></div>
              </article>
              <article>
                <span><Wind size={18} aria-hidden="true" /></span>
                <div><strong>{Math.round(currentWeatherData.wind)}</strong><small>{t("wind_kmh")}</small></div>
              </article>
              <article>
                <span><Gauge size={18} aria-hidden="true" /></span>
                <div><strong>{currentWeatherData.pressure}</strong><small>{t("Pressure")}</small></div>
              </article>
              <article>
                <span><Eye size={18} aria-hidden="true" /></span>
                <div>
                  <strong>
                    {currentWeatherData.visibility
                      ? (currentWeatherData.visibility / 1000).toFixed(1)
                      : "N/A"}
                  </strong>
                  <small>{t("visibility")}</small>
                </div>
              </article>
            </div>
          </section>

          {todayWeatherForecast.length > 0 && (
            <section className="weather-forecast-card">
              <div className="weather-section-heading">
                <div>
                  <span>{t("overview")}</span>
                  <h2>
                    {t("today_hourly_forecast", {
                      defaultValue: "Today's hourly forecast",
                    })}
                  </h2>
                </div>
                <Sun size={19} aria-hidden="true" />
              </div>
              <div className="weather-hourly-grid">
                {todayWeatherForecast.map((forecast) => (
                  <article key={forecast.dt || forecast.dt_txt}>
                    <span>{moment(forecast.dt_txt).locale(lang).format("LT")}</span>
                    <img
                      src={`https://openweathermap.org/img/wn/${forecast.weather?.[0]?.icon}@2x.png`}
                      alt={forecast.weather?.[0]?.description || "weather"}
                    />
                    <strong>{Number(forecast.main?.temp).toFixed(0)}°</strong>
                  </article>
                ))}
              </div>
            </section>
          )}

          {weatherData.length > 0 && (
            <section className="weather-forecast-card">
              <div className="weather-section-heading">
                <div>
                  <span>{t("overview")}</span>
                  <h2>{t("Upcoming_days_forecast")}</h2>
                </div>
                <Cloud size={19} aria-hidden="true" />
              </div>
              <div className="weather-daily-grid">
                {weatherData.map((forecast, index) => (
                  <article key={`${forecast.date.toISOString()}-${index}`}>
                    <span>{moment(forecast.date).locale(lang).format("ddd, MMM D")}</span>
                    <span className="weather-daily-icon">
                      {getForecastIcon(forecast.weatherCode, 42)}
                    </span>
                    <strong>{forecast.temp}°</strong>
                    <p>H: {forecast.temp_max}° · L: {forecast.temp_min}°</p>
                    <p className="weather-daily-rain">
                      <Droplets size={14} aria-hidden="true" />
                      {t("forecast_rain", { defaultValue: "Forecast rain" })}: {" "}
                      {forecast.precipitation !== null &&
                      forecast.precipitation !== undefined
                        ? `${Number(forecast.precipitation).toFixed(1)} mm`
                        : "N/A"}
                    </p>
                    <small>
                      {Math.round(forecast.wind)} km/h · {forecast.humidity}%
                      {forecast.precipitationProbability !== null &&
                      forecast.precipitationProbability !== undefined
                        ? ` · ${Math.round(forecast.precipitationProbability)}%`
                        : ""}
                    </small>
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </Container>
  );
};

export default Weather;
